import { Injectable } from '@angular/core';
import { FindQueryDto } from '@zaoblako/shared/common/dtos/find.query.dto';
import { NotificationSettingFormDto } from '@zaoblako/shared/modules/security/dtos/notifications/settings/notification.setting.form.dto';
import { NotificationSettingsFindDto } from '@zaoblako/shared/modules/security/dtos/notifications/settings/notification.settings.find.dto';
import type { UserDropdownListFindDto } from '@zaoblako/shared/modules/security/dtos/user/user-dropdown-list-find.dto';
import type { UserDropdownQueryDto } from '@zaoblako/shared/modules/security/dtos/user/user-dropdown-query.dto';
import { UserChangePasswordDto } from '@zaoblako/shared/modules/security/dtos/user/user.change.password.dto';
import { UserEmailDto } from '@zaoblako/shared/modules/security/dtos/user/user.email.dto';
import { UserFindDto } from '@zaoblako/shared/modules/security/dtos/user/user.find.dto';
import { UserListFindDto } from '@zaoblako/shared/modules/security/dtos/user/user.list.find.dto';
import { UserLoginDto } from '@zaoblako/shared/modules/security/dtos/user/user.login.dto';
import { UserLoginResponseDto } from '@zaoblako/shared/modules/security/dtos/user/user.login.response.dto';
import { UserManagerCreateDto } from '@zaoblako/shared/modules/security/dtos/user/user.manager.create.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserService extends BaseService {

  protected baseUrl = '/user';

  public login(login: UserLoginDto): Observable<UserLoginResponseDto> {
    return this.http.post<UserLoginResponseDto>('/user/login', login);
  }

  public check(): Observable<null> {
    return this.http.get<null>('/user/check');
  }

  public findEmployees(queryParams: FindQueryDto): Observable<UserListFindDto> {
    return this.http.get<UserListFindDto>('/user/employee', { params: queryParams as any });
  }

  public findClients(queryParams: FindQueryDto): Observable<UserListFindDto> {
    return this.http.get<UserListFindDto>('/user/user', { params: queryParams as any });
  }

  public findClientUsers(): Observable<UserFindDto[]> {
    return this.http.get<UserFindDto[]>('/user/client-user');
  }

  /**
   * Ищет пользователей для dropdown с пагинацией.
   *
   * @param query — search, limit, offset, includeIds, excludeIds
   * @returns страница пользователей
   */
  public findDropdown(query: UserDropdownQueryDto): Observable<UserDropdownListFindDto> {
    const params: { [key: string]: string | number | number[]; } = {
      limit: query.limit ?? 20,
      offset: query.offset ?? 0,
    };

    if (query.search) {
      params.search = query.search;
    }

    if (query.includeIds?.length) {
      params.includeIds = query.includeIds;
    }

    if (query.excludeIds?.length) {
      params.excludeIds = query.excludeIds;
    }

    return this.http.get<UserDropdownListFindDto>(`${this.baseUrl}/dropdown`, { params: params as any });
  }

  public createManager(body: UserManagerCreateDto): Observable<UserFindDto> {
    return this.http.post<UserFindDto>(`${this.baseUrl}/create-manager`, body);
  }

  public restorePassword(body: UserEmailDto): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/restore-password`, body);
  }

  public changePassword(body: UserChangePasswordDto): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/change-password`, body);
  }

  public notificationSettings(): Observable<NotificationSettingsFindDto> {
    return this.http.get<NotificationSettingsFindDto>(`${this.baseUrl}/notification-settings`);
  }

  public updateNotificationSettings(id: number, body: NotificationSettingFormDto): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/notification-settings/${id}`, body);
  }

}
